import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { BASE_URL_MS, LOGIN_URL } from "@/config/runtime";

// Porte de app/interceptor/Auth.interceptor.js — mesma semântica (um refresh em
// voo por vez, replay do request original, logout duro na falha), com três
// melhorias deliberadas sobre o original (ver docs/migracao, plano §B.5):
//   1. flag `_retried` — o legado pode entrar em loop se o gateway devolver 401
//      a um token recém-renovado (nunca marca a request como já reprocessada).
//   2. não zera `access_token` no localStorage antes do refresh dar certo — o
//      legado escreve `auth.access_token = null` antes de saber se vai funcionar
//      (Auth.interceptor.js:48), corrompendo o storage num refresh falho e
//      derrubando abas concorrentes que também leem `token-ms`.
//   3. reset real do estado de "refresh em voo" em `finally`.

export const TOKEN_STORAGE_KEY = "token-ms";
export const USER_STORAGE_KEY = "usuario-logado";
const TOKEN_URL = `${BASE_URL_MS}/auth/oauth/token`;

export interface OAuthToken {
  access_token: string;
  refresh_token: string;
  [key: string]: unknown;
}

export function readToken(): OAuthToken | null {
  try {
    return JSON.parse(localStorage.getItem(TOKEN_STORAGE_KEY) ?? "null");
  } catch {
    return null;
  }
}

export function logout(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
  window.location.assign(LOGIN_URL);
}

export const api = axios.create({ baseURL: BASE_URL_MS });

api.interceptors.request.use((config) => {
  const token = readToken();
  if (token?.access_token) {
    config.headers.set("Authorization", `Bearer ${token.access_token}`);
  } else if (config.url !== TOKEN_URL) {
    logout();
    throw new axios.Cancel("no-token");
  }
  return config;
});

let inflightRefresh: Promise<OAuthToken> | null = null;

function refresh(): Promise<OAuthToken> {
  inflightRefresh ??= (async () => {
    const current = readToken();
    if (!current?.refresh_token) throw new Error("no-refresh-token");

    const body = new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: current.refresh_token,
    });

    const { data } = await axios.post<OAuthToken>(TOKEN_URL, body, {
      headers: {
        // Higiene, não segurança — ver .env.example e plano §E.5.
        Authorization: import.meta.env.VITE_OAUTH_BASIC,
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    if (!data?.access_token || !data?.refresh_token) {
      throw new Error("bad-refresh-response");
    }
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(data));
    return data;
  })().finally(() => {
    inflightRefresh = null;
  });

  return inflightRefresh;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;

    if (!config || config.url === TOKEN_URL || error.response?.status !== 401 || config._retried) {
      return Promise.reject(error);
    }

    config._retried = true;
    try {
      await refresh();
      return await api(config);
    } catch {
      logout();
      return Promise.reject(error);
    }
  },
);
